import type { Series, Unit } from '@pharmacia/core'
import { exact } from './format.ts'
import { bar, Tooltip, useHover } from './plot-core.tsx'
import { SOLO } from './seriesColor.ts'

const ROW = 30
const GAP = 2
const RADIUS = 4

/**
 * Magnitude across named things.
 *
 * Horizontal because the names are words: rotated labels down a vertical
 * axis are read by tilting the head, and the name is half the chart. The
 * bars are sorted by the server, so the longest is at the top and the
 * ranking is the first thing the eye gets.
 *
 * Every bar carries its value as a direct label. That is also the relief
 * the palette requires: three of the light-mode series steps sit below 3:1
 * against white, so no chart here may leave colour to carry the number.
 */
export function HBarPlot({ categories, series, unit, width, truncate = 26, share = false }: {
  categories: string[]
  series: Series[]
  unit: Unit
  width: number
  /** Characters of category name before an ellipsis. */
  truncate?: number
  /** Writes each value's percentage of the first bar beside it (funnels). */
  share?: boolean
}) {
  const data = series[0]?.data ?? []
  const peak = Math.max(...data.map(v => v ?? 0), 0)
  const first = data[0] ?? 0

  const nameW = Math.min(
    Math.max(90, Math.max(...categories.map(c => Math.min(c.length, truncate))) * 6.7 + 12),
    Math.max(90, width * 0.42),
  )
  const valueW = 96
  const trackW = Math.max(10, width - nameW - valueW)
  const height = Math.max(ROW, categories.length * ROW) + 4

  const { tip, at, show, clear } = useHover()
  const clip = (c: string) => (c.length > truncate ? c.slice(0, truncate - 1) + '…' : c)

  return (
    <>
      <svg
        className="viz-svg" width={width} height={height}
        role="img" aria-label={series[0]?.label ?? 'Comparison'}
        onMouseLeave={clear}
      >
        <line className="viz-baseline" x1={nameW} x2={nameW} y1={2} y2={height - 2} />

        {categories.map((c, i) => {
          const v = data[i] ?? 0
          const w = peak > 0 ? (v / peak) * trackW : 0
          const top = i * ROW + 2
          return (
            <g key={i} opacity={at === null || at === i ? 1 : 0.55}>
              <text className="viz-cat" x={nameW - 10} y={top + ROW / 2} dy="0.32em" textAnchor="end">
                {clip(c)}
                {c.length > truncate && <title>{c}</title>}
              </text>
              <path
                className="viz-bar" fill={SOLO}
                d={bar(nameW + 1, top + GAP, w, ROW - GAP * 2, RADIUS, 'right')}
              />
              <text className="viz-label" x={nameW + w + 9} y={top + ROW / 2} dy="0.32em">
                {exact(v, unit)}
                {share && first > 0 && i > 0 ? ` · ${Math.round((v / first) * 100)}%` : ''}
              </text>
              <rect
                className="viz-hit" x={0} y={top} width={width} height={ROW}
                onMouseMove={e => show(i, {
                  title: c,
                  rows: [{ color: SOLO, label: series[0]?.label ?? 'Value', value: exact(v, unit) }],
                }, e)}
              />
            </g>
          )
        })}
      </svg>
      <Tooltip tip={tip} />
    </>
  )
}
